/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

interface IInject<T> {
    classType: new () => T;
    instance: T;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
export const containerContext = React.createContext<Container>(null as any);

/** decorator */
export function inject(type: new (...args: any) => any) {
    return (target: any, _: any, parameterIndex: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        target.$inject ??= [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        target.$inject[parameterIndex] = type;
    };
}

/** decorator */
export function injectable(type: "singleton" | "transient") {
    return (componentClass: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        componentClass._injectableType = type;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return componentClass;
    };
}

export class Container {
    public allowAutoInjection = false;

    private injects: IInject<any>[] = [];

    public get<T>(classType: new (...args: any) => T): T {
        const existing = this.injects.find(x => x.classType === classType);
        if (!existing) {
            if (!this.allowAutoInjection) {
                throw new Error(
                    `Container doesn't allow singleton auto-injection for ${classType.toString()}`
                );
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if ((classType as any)._injectableType !== "singleton") {
                throw new Error(
                    `Cannot inject a non-singleton ${classType.toString()}`
                );
            }
            const instance = this.create(classType);
            this.set(classType, instance);
            return instance;
        }
        return existing.instance as T;
    }

    public createTransient<T>(classType: new (...args: any) => T): T {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if ((classType as any)._injectableType !== "transient") {
            throw new Error(
                `Cannot create a non-transient ${classType.toString()}`
            );
        }
        return this.create(classType);
    }

    private create<T>(classType: new (...args: any) => T): T {
        const params: any[] = [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if ((classType as any).$inject) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            for (const type of (classType as any).$inject) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                params.push(this.get(type));
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const instance = new classType(...params);
        return instance;
    }

    public set<T>(classType: new (...args: any) => T, instance: T): void {
        this.injects.push({
            classType,
            instance
        });
    }
}
